// The view area of the web app and what views share: timers that stop on navigation, rendering and formatting.
import { Session } from './session.js';
import { type Html, Ui } from './ui.js';

const { h, setHtml, attempt } = Ui;

const view = Ui.el('#view');

let timers: number[] = [];
const rendered = new WeakMap<Element, string>();

/** Runs `fn` every `ms` until the next navigation. */
const every = (ms: number, fn: () => void): void => {
  timers.push(window.setInterval(fn, ms));
};

/** Stops the previous view's timers before the next one renders. */
const reset = (): void => {
  timers.forEach(clearInterval);
  timers = [];
  rendered.delete(view);
};

const formatDate = (ms: number): string =>
  new Date(ms).toLocaleString([], {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatTime = (ms: number): string => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const formatCountdown = (ms: number): string => {
  if (ms <= 0) return 'Now!';
  const s = Math.floor(ms / 1000);
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  return hours > 0
    ? `${hours}h ${String(minutes).padStart(2, '0')}m`
    : `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/** Keeps every element matching `selector` counting down to `until`. Call the returned tick right after re-rendering one. */
const countdown = (selector: string, until: (e: HTMLElement) => number): (() => void) => {
  const tick = () =>
    document.querySelectorAll<HTMLElement>(selector).forEach(e => {
      e.textContent = formatCountdown(until(e) - Session.serverNow());
    });
  tick();
  every(1000, tick);
  return tick;
};

/** Re-runs a view's loader every few seconds, so new sign-ups and fights show up on their own. */
const poll = (load: () => Promise<void>, ms: number): void => every(ms, () => void load().catch(() => undefined));

/** Leaves the element alone when the markup hasn't changed, so polling doesn't flash it. */
const renderIfChanged = (target: Element, html: Html): boolean => {
  const markup = html.toString();
  if (rendered.get(target) === markup) return false;
  rendered.set(target, markup);
  setHtml(target, html);
  return true;
};

/** Runs the action on submit, with the form's first input at hand. */
const onSubmit = (selector: string, action: (input: HTMLInputElement) => Promise<void> | void): void => {
  const form = Ui.elOf(HTMLFormElement, selector);
  form.onsubmit = e => {
    e.preventDefault();
    void attempt(async () => action(Ui.elOf(HTMLInputElement, 'input', form)));
  };
};

/** Disables the button while its action runs, so it can't be sent twice. */
const busy = (btn: HTMLButtonElement, action: () => Promise<void>): Promise<void> =>
  attempt(async () => {
    btn.disabled = true;
    try {
      await action();
    } finally {
      btn.disabled = false;
    }
  });

const avatarHtml = (url: string | null): Html =>
  url ? h`<img class="avatar" src="${url}" alt="">` : h`<span class="avatar"></span>`;

const openMatch = ({ matchId }: { matchId: string }): void => {
  location.href = `/match.html?id=${matchId}`;
};

export const Page = {
  view,
  every,
  reset,
  formatDate,
  formatTime,
  countdown,
  poll,
  renderIfChanged,
  onSubmit,
  busy,
  avatarHtml,
  openMatch,
};
